/**
 * MCP host token relay — port of legacy `RaptorChatService.setMCPHostToken`
 * (`raptor-shell-ui/.../core/services/raptor-chat.service.ts`) and the
 * RUM-interceptor trigger gated by `sendTokenForMCPClient`
 * (`.../core/interceptors/rum.interceptor.ts`).
 *
 * When legacy `sendTokenForMCPClient` is on, the shell acquires a token for the
 * Transactions Concierge scope and POSTs it to the MCP chat host's
 * `api/mcp/set-token` endpoint (with `x-server-name: Transactions` +
 * `x-user-email`) so the assistant backend can call Transactions APIs on the
 * user's behalf. Legacy restricts this to users who hold a client whose name is
 * in a fixed allowlist ("SCP Demo"). The flag is QA-only (dev/prod = false).
 *
 * Legacy fires this from the RUM interceptor on every RUM-authed request (fresh
 * token each time). The React port fires once post-login (after the client list
 * resolves, so the concierge token is fresh) — sufficient for the demo flow;
 * a periodic refresh can be layered on if the MCP host needs a rolling token.
 */
import { acquireMfeTokenAcrossClientIds } from '@/auth/multi-client-token';
import { getUserProfile } from '@/auth/user-profile';
import { useClientStore } from '@/client/client-store';
import { getTracker } from '@/telemetry/tracker';
import { isEnvFlagTrue } from '@/config/env-flags';

const SEND_TOKEN = isEnvFlagTrue(import.meta.env.VITE_SEND_TOKEN_FOR_MCP_CLIENT);
const CONCIERGE_SCOPE = (import.meta.env.VITE_TRANSACTIONS_CONCIERGE_SCOPE ?? '').trim();
// Legacy POSTs set-token to the shell-ui `iFrameMCPChatUrl` (e.g. the
// same-origin `https://raptorq.ey.com/chat/`), which is DISTINCT from the
// shell-header chatbot iframe host (`VITE_MCP_CHAT_URL`, the MCP container app).
// Prefer the dedicated set-token base; fall back to the chat URL if unset.
const MCP_SET_TOKEN_BASE = (
  import.meta.env.VITE_MCP_SET_TOKEN_URL ||
  import.meta.env.VITE_MCP_CHAT_URL ||
  ''
).trim();

/** Legacy `RaptorChatService.allowedClients`. */
const ALLOWED_CLIENTS: readonly string[] = ['SCP Demo'];

let inFlight = false;

function hasAllowedClient(): boolean {
  return useClientStore
    .getState()
    .available.some((client) => ALLOWED_CLIENTS.includes(client.name));
}

function setTokenEndpoint(): string {
  const base = MCP_SET_TOKEN_BASE.endsWith('/') ? MCP_SET_TOKEN_BASE : `${MCP_SET_TOKEN_BASE}/`;
  return `${base}api/mcp/set-token`;
}

/**
 * Relay a Transactions Concierge token to the MCP host. No-op unless the feature
 * flag is on, the scope + host URL are configured, and the user holds an
 * allowlisted client. Best-effort: failures are logged, never thrown.
 */
export async function setMcpHostToken(): Promise<void> {
  if (!SEND_TOKEN || !CONCIERGE_SCOPE || !MCP_SET_TOKEN_BASE) return;
  if (inFlight || !hasAllowedClient()) return;

  inFlight = true;
  try {
    const token = await acquireMfeTokenAcrossClientIds([CONCIERGE_SCOPE]);
    if (!token) return;
    await fetch(setTokenEndpoint(), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'x-server-name': 'Transactions',
        'x-user-email': getUserProfile()?.email ?? '',
        'Content-Type': 'application/json',
      },
      body: '{}',
    });
    getTracker().event('mcp_host_token_set');
  } catch (err) {
    getTracker().error(err instanceof Error ? err : new Error(String(err)), {
      context: 'mcp_set_host_token',
    });
  } finally {
    inFlight = false;
  }
}
